import React from 'react';
import { Calculator, Info } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const AlgorithmInfo = () => {
  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="centroid" className="border-slate-200">
        <AccordionTrigger className="text-slate-700 hover:text-slate-900 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Центроид полигона</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="text-slate-600 space-y-3 text-sm">
          <p>
            <strong>Центроид</strong> — это геометрический центр фигуры, который представляет собой среднее арифметическое положение всех её точек. Вычисляется через разбиение полигона на треугольники. Производится расчет среднего значения кластера данных по всем треугольникам.
          </p>
          <p className="text-xs">
            <strong>Шаг 1:</strong> Вычисляем площадь полигона по формуле Гаусса (Shoelace formula):
          </p>
          <div className="bg-slate-100 p-2 rounded font-mono text-xs">
            A = ½ × |Σ(xᵢ × yᵢ₊₁ - xᵢ₊₁ × yᵢ)|
          </div>
          <p className="text-xs">
            <strong>Шаг 2:</strong> Вычисляем координаты центроида:
          </p>
          <div className="bg-slate-100 p-2 rounded font-mono text-xs space-y-1">
            <p>Cx = (1/6A) × Σ(xᵢ + xᵢ₊₁) × (xᵢ×yᵢ₊₁ - xᵢ₊₁×yᵢ)</p>
            <p>Cy = (1/6A) × Σ(yᵢ + yᵢ₊₁) × (xᵢ×yᵢ₊₁ - xᵢ₊₁×yᵢ)</p>
          </div>
          <p className="text-xs text-slate-500">
            где (xᵢ, yᵢ) — координаты i-й вершины полигона, суммирование по всем рёбрам.
          </p>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="multipolygon" className="border-slate-200">
        <AccordionTrigger className="text-slate-700 hover:text-slate-900 text-sm">
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-slate-500" />
            <span>Сложные границы (MultiPolygon)</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="text-slate-600 space-y-3 text-sm">
          <p>
            Границы города часто состоят из нескольких частей (острова, эксклавы). 
            Для них вычисляется <strong>взвешенный центроид</strong>:
          </p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>Вычисляем центроид каждой части отдельно</li>
            <li>Вычисляем площадь каждой части</li>
            <li>Итоговый центроид = среднее, взвешенное по площади</li>
          </ol>
          <div className="bg-slate-100 p-2 rounded font-mono text-xs">
            C_итог = Σ(Cᵢ × Aᵢ) / Σ(Aᵢ)
          </div>
          <p className="text-xs text-slate-500">
            Большие части территории сильнее влияют на положение центра.
          </p>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="visualization" className="border-slate-200">
        <AccordionTrigger className="text-slate-700 hover:text-slate-900 text-sm">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-slate-500" />
            <span>Что показано на карте</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="text-slate-600 space-y-3 text-sm">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-3 h-3 rounded-full bg-sky-500 mt-0.5 flex-shrink-0"></div>
              <span className="text-xs"><strong>Синий контур</strong> — реальные границы территории из OpenStreetMap</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500 mt-0.5 flex-shrink-0"></div>
              <span className="text-xs"><strong>Оранжевые точки</strong> — выборка точек контура для наглядности (не все вершины)</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-4 h-0.5 border-t-2 border-dashed border-red-500 mt-1.5 flex-shrink-0"></div>
              <span className="text-xs"><strong>Красные линии</strong> — визуализация связи точек с центром</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500 mt-0.5 flex-shrink-0"></div>
              <span className="text-xs"><strong>Красный маркер</strong> — вычисленный центроид</span>
            </div>
          </div>
          <p className="text-xs bg-amber-50 p-2 rounded mt-2">
            <strong>Примечание:</strong> Оранжевые точки — это лишь визуализация. 
            Реальный расчёт использует все точки контура (тысячи координат).
          </p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default AlgorithmInfo;
